<template>
  <div id="folder-picker">
    <div class="o-modal">
      <div class="o-modal-content">
        <div class="o-modal-header">
          <a
            @click="$store.commit('dataJoiner/setStateProperty', { property: 'showFolderPicker', value: false })"
            class="close clickable"
          ><i class="far fa-times"></i></a>
          <p class="lead">Save to Every.Farm</p>
        </div><!-- /.o-modal-header -->
        <div class="o-modal-body">
          <b-alert
            variant="success"
            show
            v-if="saved"
          >Successfully saved to Every.Farm!</b-alert>
          <div class="card p-1">
            <p class="text-center mb-1"><strong>Name Dataset</strong></p>
            <b-form-input
              v-model="datasetName"
              class="mb-3"
            ></b-form-input>
            <p class="text-center mb-1"><strong>Choose a Location</strong></p>
            <div
              class="mb-2"
            >
              <span
                v-for="(crumb, i) in breadcrumbs"
                v-bind:key="i"
              >
                <b-badge
                  pill
                  variant="primary"
                  @click="launchFolder(crumb, true)"
                  class="clickable"
                >{{ crumb.name }}</b-badge><span v-if="(i + 1) !== breadcrumbs.length"> / </span>
              </span>
            </div>
            <div class="card p-2 scroll">
              <!-- FOLDERS -->
              <div class="list-group mb-2 folders" v-if="!loadingFolders">
                <div
                  v-for="(folder, i) in $store.state.dataJoiner.folders"
                  v-bind:key="i"
                  class="list-container"
                >
                  <a
                    @click="launchFolder(folder)"
                    class="list-group-item list-group-item-action clickable mb-2 folder"
                  >
                    <p class="lead mb-1"><i class="fas fa-folder"></i> {{ folder.name }}</p>
                    <p class="mb-1">{{ folder.description }}</p>
                  </a>
                </div>
              </div><!-- /.list-group -->
              <!-- DATASETS -->
              <div class="list-group sets" v-if="!loadingDatasets">
                <div
                v-for="(dataset, i) in $store.state.dataJoiner.datasets"
                v-bind:key="i"
                class="list-container"
                >
                  <a class="list-group-item list-group-item-action handle mb-2">
                    <p class="lead mb-1"><i v-if="dataset.dynamic" class="fas fa-satellite-dish" v-b-tooltip.hover.top="'Dynamic Dataset'"></i> {{ dataset.name }}</p>
                    <p class="mb-1">{{ dataset.description }}</p>
                  </a>
                </div>
              </div><!-- /.list-group -->
            </div><!-- /.card -->
          </div><!-- /.card -->
        </div><!-- /.o-modal-body -->
        <div class="o-modal-footer">
          <div class="float-right">
            <button
              v-if="!saving"
              @click="saveToEveryFarm()"
              class="btn btn-success"
            >
              Save to Every.Farm
            </button>
            <button
              v-else
              @click="saveToEveryFarm()"
              class="btn btn-light"
            >
              <i class="fas fa-sync fa-spin"></i> Saving to Every.Farm...
            </button>
          </div><!--/.float-right -->
        </div><!-- /.o-modal-footer -->
      </div><!-- /.o-modal-content -->
    </div><!-- /.o-modal -->
  </div><!-- /#dataset-picker -->
</template>

<script>
import firebase from 'firebase/compat/app'
import { deriveHeaders } from '@/lib/deriveHeaders'
const fb = require('@/firebaseConfig.js')

export default {
  data () {
    return {
      datasetName: 'Joined Dataset',
      loadingFolders: true,
      loadingDatasets: true,
      saving: false,
      saved: false
    }
  },
  computed: {
    breadcrumbs: {
      get () {
        return this.$store.state.dataJoiner.breadcrumbs
      },
      set (value) {
        this.$store.commit('dataJoiner/setStateProperty', { property: 'breadcrumbs', value: value })
      }
    },
    loadingFolderPicker: {
      get () {
        return this.$store.state.dataJoiner.loadingFolderPicker
      },
      set (value) {
        this.$store.commit('dataJoiner/setStateProperty', { property: 'loadingFolderPicker', value: value })
      }
    }
  },
  created () {
    this.fetchDatasets()
    this.fetchFolders()
  },
  destroyed () {
    this.loadingFolderPicker = false
  },
  methods: {
    async saveToEveryFarm () {
      this.saving = true
      const postName = this.datasetName

      const geoJsonConstructor = {
        type: 'FeatureCollection',
        features: []
      }

      // The first row of allJsonDataArray is the headers map (unshifted
      // by processNewFile); the rest are real data points.
      this.$store.state.dataJoiner.allJsonDataArray.forEach((dataPoint, i) => {
        if (i > 0) {
          const featureConstructor = {
            type: 'Feature',
            properties: dataPoint,
            geometry: {
              type: 'Point',
              coordinates: [
                dataPoint.longitude,
                dataPoint.latitude
              ]
            }
          }
          geoJsonConstructor.features.push(featureConstructor)
        }
      })

      if (geoJsonConstructor.features.length === 0) {
        // Never save an empty dataset (it happened silently for location-only datasets).
        this.$store.commit('setAlert', { active: true, message: 'The joined result has no points, so nothing was saved. For datasets that only record locations, use Stack instead of Join.', level: 'alert-warning', timeout: 12000 })
        this.saving = false
        return
      }
      try {
        const farmId = this.$store.state.farm && this.$store.state.farm.id
        const path = `datasets/${farmId}/joined-${Date.now()}.geojson`
        await firebase.storage().ref(path).putString(
          JSON.stringify(geoJsonConstructor), 'raw',
          {
          contentType: 'application/json',
          // usageFinalize attributes bytes by customMetadata.userId —
          // without it, plugin-generated datasets aren't counted.
          customMetadata: { userId: (firebase.auth().currentUser && firebase.auth().currentUser.uid) || '' }
        }
        )
        const feats = geoJsonConstructor.features
        const headers = deriveHeaders({ features: feats })
        const uid = this.$store.state.currentUser && this.$store.state.currentUser.uid
        await fb.datasetsCollection.add({
          name: postName,
          description: 'Joined dataset (Data Joiner).',
          headers: headers,
          farmId: farmId,
          createdOn: new Date(),
          createdBy: uid,
          updatedOn: new Date(),
          updatedBy: uid,
          file: { geoJsonPath: path, path: path, originalFileName: 'joined.geojson' },
          folderId: this.$store.state.dataJoiner.folder.id || 'root',
          dynamic: false,
          vizSettings: {
            colorHeader: false,
            colorBy: 'solid'
          }
        })
        this.saved = true
        setTimeout(() => {
          this.$store.commit('dataJoiner/setStateProperty', { property: 'showFolderPicker', value: false })
        }, 5000)
      } catch (err) {
        console.log(err)
        this.saving = false
      }
    },
    fetchDatasets () {
      this.loadingDatasets = true
      const farmId = this.$store.state.farm && this.$store.state.farm.id
      const folderId = this.$store.state.dataJoiner.folder.id
      fb.datasetsCollection
        .where('farmId', '==', farmId)
        .where('folderId', '==', folderId)
        .orderBy('updatedOn', 'desc')
        .get()
        .then(snap => {
          const datasets = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          this.$store.commit('dataJoiner/setStateProperty', { property: 'datasets', value: datasets })
          this.loadingDatasets = false
        }).catch(err => {
          console.log(err.message)
          this.loadingDatasets = false
        })
    },
    fetchFolders () {
      this.loadingFolders = true
      const farmId = this.$store.state.farm && this.$store.state.farm.id
      const folderId = this.$store.state.dataJoiner.folder.id
      fb.foldersCollection
        .where('farmId', '==', farmId)
        .where('folderId', '==', folderId)
        .orderBy('name', 'asc')
        .get()
        .then(snap => {
          const folders = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          this.$store.commit('dataJoiner/setStateProperty', { property: 'folders', value: folders })
          this.loadingFolders = false
        }).catch(err => {
          console.log(err.message)
          this.loadingFolders = false
        })
    },
    launchFolder (folder, fromCrumb) {
      if (this.breadcrumbs.length < 1) {
        this.breadcrumbs = [{ name: 'Root', id: 'root' }]
      }
      if (fromCrumb) {
        const newBreadcrumbs = []
        this.breadcrumbs.some(crumb => {
          if (crumb.id !== folder.id) {
            newBreadcrumbs.push(crumb)
          } else {
            newBreadcrumbs.push(crumb)
            return true
          }
        })
        this.breadcrumbs = newBreadcrumbs
      } else {
        this.breadcrumbs.push(folder)
      }
      this.$store.commit('dataJoiner/setStateProperty', { property: 'folder', value: folder })
      this.fetchDatasets()
      this.fetchFolders()
    },
    launchDataset (dataset) {
      this.$store.commit('dataJoiner/setStateProperty', { property: 'dataset', value: dataset })
      this.$store.commit('dataJoiner/showOptions', true)
    }
  }
}
</script>

<style scoped>
/* Cap the picker so a long folder list scrolls inside the modal
   instead of pushing the Save button below the fold. */
.scroll {
  max-height: 260px;
  overflow-y: auto;
}
.card > .list-group {
  border-top: none;
  border-bottom: none;
}
.folder {
  background-color: #eee;
}

.badge.clickable:hover {
  box-shadow: 1px 2px 4px rgba(0,0,0,0.5);
}
</style>
